/**
 * Changed-row marks between two persisted outline snapshots of the same
 * target. Rows match by id first; rows the planner re-created under a new
 * id match by their text (block purpose or lesson title), so a full
 * re-proposal does not mark every row as new.
 */

import type { Outline, OutlineBlock, OutlineLesson, OutlineSnapshotPart } from '../../api/authorWithAiApi';

export type RowMark = 'added' | 'retyped' | null;

export interface MarkedRow<T> {
  item: T;
  mark: RowMark;
}

export interface RowDiff<T> {
  rows: MarkedRow<T>[];
  removed: T[];
}

const normalize = (value: string): string => value.trim().replace(/\s+/g, ' ').toLowerCase();

function matchRows<T extends { id: string }>(
  previous: readonly T[] | null,
  next: readonly T[],
  text: (item: T) => string,
): { matches: Map<T, T>; removed: T[] } {
  const matches = new Map<T, T>();
  if (!previous) return { matches, removed: [] };
  const unmatched = new Set(previous);
  const byId = new Map(previous.map((item) => [item.id, item]));
  for (const item of next) {
    const prior = byId.get(item.id);
    if (prior && unmatched.has(prior)) {
      matches.set(item, prior);
      unmatched.delete(prior);
    }
  }
  for (const item of next) {
    if (matches.has(item)) continue;
    const prior = [...unmatched].find((candidate) => normalize(text(candidate)) === normalize(text(item)));
    if (prior) {
      matches.set(item, prior);
      unmatched.delete(prior);
    }
  }
  return { matches, removed: [...unmatched] };
}

/** Block rows of one lesson; no marks when there is no earlier snapshot. */
export function diffLessonBlocks(previous: OutlineLesson | null, next: OutlineLesson): RowDiff<OutlineBlock> {
  if (!previous) return { rows: next.blocks.map((item) => ({ item, mark: null })), removed: [] };
  const { matches, removed } = matchRows(previous.blocks, next.blocks, (block) => block.purpose);
  return {
    rows: next.blocks.map((item) => {
      const prior = matches.get(item);
      return { item, mark: !prior ? 'added' : prior.type !== item.type ? 'retyped' : null };
    }),
    removed,
  };
}

/** Lesson rows of a course outline. */
export function diffCourseLessons(previous: Outline | null, next: Outline): RowDiff<OutlineLesson> {
  if (!previous) return { rows: next.lessons.map((item) => ({ item, mark: null })), removed: [] };
  const { matches, removed } = matchRows(previous.lessons, next.lessons, (lesson) => lesson.title);
  return { rows: next.lessons.map((item) => ({ item, mark: matches.has(item) ? null : 'added' })), removed };
}

/** What a snapshot box shows: one lesson's blocks, or the course's lesson list. */
export type SnapshotTarget =
  | { kind: 'lesson'; key: string; lesson: OutlineLesson; position: number; total: number }
  | { kind: 'course'; key: string; outline: Outline };

export function snapshotTarget(part: OutlineSnapshotPart): SnapshotTarget {
  const { outline } = part;
  const total = outline.lessons.length;
  if (total === 1) {
    // A single-lesson build keeps its outline id even when the lesson is re-created.
    return { kind: 'lesson', key: `single:${outline.id}`, lesson: outline.lessons[0]!, position: 1, total };
  }
  const index = part.focusedLessonId ? outline.lessons.findIndex((lesson) => lesson.id === part.focusedLessonId) : -1;
  const lesson = outline.lessons[index];
  if (lesson) return { kind: 'lesson', key: `lesson:${lesson.id}`, lesson, position: index + 1, total };
  return { kind: 'course', key: `course:${outline.id}`, outline };
}

/** For each snapshot (by message id), the earlier snapshot of the same target in the thread. */
export function previousSnapshots(
  snapshots: ReadonlyArray<{ messageId: string; part: OutlineSnapshotPart }>,
): Map<string, OutlineSnapshotPart | null> {
  const lastByKey = new Map<string, OutlineSnapshotPart>();
  const result = new Map<string, OutlineSnapshotPart | null>();
  for (const { messageId, part } of snapshots) {
    const key = snapshotTarget(part).key;
    result.set(messageId, lastByKey.get(key) ?? null);
    lastByKey.set(key, part);
  }
  return result;
}
